import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { getTargetBuildRuns, type BuildRun } from '../../lib/api/egress.js';

/**
 * Recent build+deploy runs for a masthead.
 *
 * `publication_build_runs` has recorded state, lane, commit, error and timings
 * for every build since migration 317, and none of it reached the UI. That is
 * what made the 2026-08-26 OOM hard to see: the dashboard reported the
 * dispatch envelope ("build-deploy returned 500") while the real cause — a
 * V8 out-of-memory kill — sat one table away.
 *
 * Deliberately read-only, and deliberately unclamped on the error text: for a
 * build the error IS the diagnosis, and the lines that name the cause are the
 * ones a two-line clamp removes.
 */
const STATE: Record<string, { label: string; cls: string }> = {
  deployed:   { label: 'Deployed',   cls: 'bg-success-50 text-success-700 border-success-200' },
  failed:     { label: 'Failed',     cls: 'bg-danger-50 text-danger-700 border-danger-200' },
  building:   { label: 'Building',   cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  deploying:  { label: 'Deploying',  cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  claimed:    { label: 'Queued',     cls: 'bg-grey-100 text-grey-700 border-grey-200' },
  superseded: { label: 'Superseded', cls: 'bg-grey-100 text-grey-600 border-grey-200' },
  shadow:     { label: 'Shadow',     cls: 'bg-warning-50 text-warning-700 border-warning-200' },
};

function ms(v: number | null): string {
  if (v === null) return '—';
  return v < 1000 ? `${v}ms` : `${(v / 1000).toFixed(1)}s`;
}

function when(ts: string | null): string {
  if (!ts) return '—';
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function Row({ run }: { run: BuildRun }) {
  const st = STATE[run.state] || { label: run.state, cls: 'bg-grey-100 text-grey-600 border-grey-200' };
  const isMain = run.destination_type === 'main_branch';
  return (
    <div className="py-3 border-b border-grey-100 last:border-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${st.cls}`}>
          {run.state === 'failed' && <AlertTriangle className="h-3 w-3" />}
          {run.state === 'deployed' && <CheckCircle2 className="h-3 w-3" />}
          {['building', 'deploying', 'claimed'].includes(run.state) && <Loader2 className="h-3 w-3 animate-spin" />}
          {st.label}
        </span>
        <span className="text-sm font-semibold text-grey-900">
          {isMain ? 'Production' : 'Preview'}
        </span>
        {run.lane === 'fast' && (
          <span className="text-xs text-grey-500" title="Price-sensitive or coverage-customer content">
            fast lane
          </span>
        )}
        <span className="flex-1" />
        <span className="inline-flex items-center gap-1 text-xs text-grey-500">
          <Clock className="h-3 w-3" />
          {when(run.deployed_at || run.finished_at || run.started_at)}
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-grey-500">
        {run.content_version !== null && <span className="tabular-nums">v{run.content_version}</span>}
        {run.pages_built !== null && <span className="tabular-nums">{run.pages_built.toLocaleString()} pages</span>}
        <span className="tabular-nums">build {ms(run.build_ms)}</span>
        <span className="tabular-nums">deploy {ms(run.deploy_ms)}</span>
        {run.commit_sha && <span className="tabular-nums">commit {run.commit_sha.slice(0, 7)}</span>}
        {run.cf_version_id && (
          <span className="tabular-nums" title={run.cf_version_id}>
            {/* test uploads carry an alias and no traffic; main carries a deployment */}
            {run.cf_kind === 'deployment' ? 'deployment' : `alias ${run.cf_preview_alias || '—'}`}{' '}
            {run.cf_version_id.slice(0, 8)}
          </span>
        )}
      </div>

      {run.error_message && (
        <pre className="mt-1.5 text-[11px] font-mono text-danger-700 bg-danger-50 border border-danger-200 rounded p-2 whitespace-pre-wrap break-words overflow-x-auto">
          {run.error_message}
        </pre>
      )}
    </div>
  );
}

export function BuildHistoryPanel({ targetId }: { targetId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['target-build-runs', targetId],
    queryFn: () => getTargetBuildRuns(targetId),
    refetchInterval: 30_000,
  });

  if (isLoading || error || !data) return null;
  if (data.runs.length === 0) return null;

  return (
    <div className="rounded-xl border border-grey-200 bg-surface p-6 shadow-md">
      <h3 className="text-base font-bold text-grey-900 mb-1">Recent builds</h3>
      <p className="text-xs text-grey-500 mb-3">
        Every build attempt, newest first — including the ones that failed and
        what they said.
      </p>
      <div>
        {data.runs.map((r) => <Row key={r.run_id} run={r} />)}
      </div>
    </div>
  );
}
